import { nextTick, onBeforeUnmount, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { isValidQuerySelector } from '@/lib/utils/browserHelpers'
import pageTitle from '@/lib/utils/page-title'
import scroll from '@/lib/utils/scroll'

import { useContentful } from '@/components/common/contentful/composables/useContentful'
import { useContentfulInit } from '@/components/common/contentful/composables/useContentfulInit'

import { useAnalytics } from '@/composables/useAnalytics'
import { useEmitter } from '@/composables/useEmitter'
import { useErrorService } from '@/composables/useErrorService'
import { useAppStore } from '@/stores/app'
import { useAsmStore } from '@/stores/asm'
import { useCartStore } from '@/stores/cart'
import { usePunchoutStore } from '@/stores/punchout'
import { useSessionStore } from '@/stores/session'

export function useAppInit() {
  const router = useRouter()
  const emitter = useEmitter()
  const analytics = useAnalytics()
  const errorService = useErrorService()
  const { contentfulInit, hubspotTrackerTmpl } = useContentfulInit()
  const contentful = useContentful()

  const sessionStore = useSessionStore()
  const punchoutStore = usePunchoutStore()
  const cartStore = useCartStore()
  const appStore = useAppStore()
  const asmStore = useAsmStore()

  const removeGlobalEventListeners = () => {
    emitter.off('auth.notAuthorized')
    emitter.off('route.after')
  }

  const addGlobalEventListeners = () => {
    emitter.on('auth.notAuthorized', async (errors = []) => {
      const isUserLoggedIn = sessionStore.isUserLoggedIn
      if (isUserLoggedIn) {
        await sessionStore.logout()
      } else {
        await sessionStore.resetApiSession()
        await cartStore.clearCart()
      }
      appStore.setNotAuthorizedErrors(errors || [])
      if (isUserLoggedIn) {
        router.push({ name: 'authentication' })
      }
      await sessionStore.checkSession()
      if (!isUserLoggedIn) window.location.reload()
    })

    emitter.on('route.after', async (payload) => {
      pageTitle.update(payload.to)
      await nextTick()
      if (payload.to.meta.isScrollingUp) await scroll.pageToTop()
      if (payload.to.hash && isValidQuerySelector(payload.to.hash)) {
        setTimeout(() => {
          const element = document.querySelector(payload.to.hash)
          if (element) {
            scroll.scrollToElement(element)
          }
        }, 1)
      }
      analytics.sendPageView()
    })
  }

  const initialize = async () => {
    const queryParams = new URLSearchParams(window.location.search)
    const punchoutErrorMessage = queryParams.get('punchoutSessionErrorMessage')
    if (punchoutErrorMessage) {
      appStore.setCriticalViewData({
        templateName: 'punchout',
        data: punchoutErrorMessage,
      })
      return
    }

    appStore.toggleLoader(true)

    sessionStore.$subscribe((mutation, state) => {
      errorService.configureClient(state.email)
    })

    await Promise.all([punchoutStore.initPunchoutState(), asmStore.initAsmState()])
    await contentfulInit()

    if (!punchoutStore.isPunchoutSession) await sessionStore.checkSession()

    Promise.all([cartStore.initCartState(), sessionStore.initSessionState()]).then(() => {
      if (window.localStorage) {
        window.localStorage.setItem('bioForceReloadOnError', true)
      }
      addGlobalEventListeners()
      appStore.isFullInitialized = true
    })

    const redirect = contentful.service.getRedirect(window.location.pathname)
    if (redirect) window.location.href = redirect

    appStore.setInitialized(true)
    appStore.toggleLoader(false)
  }

  onMounted(() => {
    initialize()
  })

  onBeforeUnmount(() => {
    removeGlobalEventListeners()
  })

  return {
    hubspotTrackerTmpl,
  }
}
